import { useState, useRef, useEffect, Children } from 'react';
import { keyframes, css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Animation } from '../../../theme/Animation.es.js';
import { ColorsCSS } from '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import { Zindexes } from '../../../theme/Zindexes.es.js';
import '../../../theme/ZinniaStylesReset.es.js';
import { Icon } from '../Icon/Icon.es.js';
import { useUnscrollableBody } from './useUnscrollableBody.es.js';
import { useFocusTrap } from './useFocusTrap.es.js';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
const Styles = labelStyles("Modal", {
  root: css`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    // Setting height is redundant in most browsers since top/bottom are set,
    // but necessary for Safari to properly apply align-items.
    height: 100%;
    z-index: ${Zindexes.modalOverlay};
    opacity: 0;
    display: grid;
    align-items: center;
    justify-items: center;
  `,
  rootIn: css`
    animation: ${fadeIn} ${Animation.transitionDuration}
      ${Animation.transitionTimingFunction} forwards;
  `,
  rootOut: css`
    animation: ${fadeOut} ${Animation.transitionDuration}
      ${Animation.transitionTimingFunction} forwards;
  `,
  overlay: css`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${ColorsCSS.neutral800};
    opacity: 0.8;
    z-index: 1;
  `,
  closeButton: css`
    display: block;
    color: ${ColorsCSS.neutral600};
    transition: ${Animation.transitionValue};
    &:hover,
    &:focus {
      color: ${ColorsCSS.blue};
    }
  `,
  modal: css`
    position: relative;
    z-index: 2;
    max-height: 100vh;
    // Prevent margin collapsing of children
    padding: 1px;
    // Ensure children shink by default
    display: flex;
  `
});
const Modal = (props) => {
  const canClose = props.canClose !== false;
  const [isOpen, setIsOpen] = useState(true);
  const modalRef = useRef(null);
  useUnscrollableBody({ shouldAllowScroll: props.canScrollBody === true });
  const onClose = () => {
    if (canClose) {
      setIsOpen(false);
    }
  };
  const onAnimationEnd = () => {
    if (isOpen === false) {
      props.onClosed();
    }
  };
  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keyup", onEscape, { capture: true });
    return () => {
      window.removeEventListener("keyup", onEscape, { capture: true });
    };
  });
  useFocusTrap(modalRef);
  const overlay = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    jsx("div", { css: Styles.overlay, "data-testid": "ModalOverlay", onClick: onClose })
  );
  const closeButton = !!canClose && jsx(
    "button",
    { css: Styles.closeButton, "aria-label": "Close modal", onClick: onClose, type: "button", "data-zds": true },
    jsx(Icon, { isBlock: true, name: "formX", size: 32 })
  );
  const onlyChild = props.children && Children.only(props.children);
  const children = Object.assign(Object.assign({}, onlyChild), { props: Object.assign(Object.assign({}, props.children.props || {}), { closeButton }) });
  const modal = jsx("div", { "aria-label": props.ariaLabel, "aria-live": "polite", "aria-modal": "true", css: Styles.modal, ref: modalRef, role: "dialog", "data-zds": true }, children);
  return jsx(
    "div",
    { css: [Styles.root, isOpen ? Styles.rootIn : Styles.rootOut], "data-testid": "Modal", onAnimationEnd, "data-zds": true },
    jsx(
      "div",
      { "data-zds": true },
      overlay,
      modal
    )
  );
};

export { Modal };
//# sourceMappingURL=Modal.es.js.map
